import { useEffect, useRef, useState } from 'react';
import { activateOptimizelyExperiment } from 'services/optimizely';

/**
 * Enables an Optimizely experiment or page with the given `name` to be manually activated,
 * in response to a specified element being scrolled into view
 *
 * Returns a ref that should be attached to an HTML element for this purpose
 *
 * @param pageName The API name of the page / experiment / campaign to activate
 * @param options An optional object containing options to pass to the `IntersectionObserver` instance
 *
 * @see https://support.optimizely.com/hc/en-us/articles/4410289605133-Locate-IDs-for-use-with-the-Experimentation-APIs
 *  for more information about the name that this hook receives
 */
export default function useActivateOnScroll<ElementType extends HTMLElement>(
  pageName: string,
  options?: IntersectionObserverInit
) {
  const elementRef = useRef<ElementType>(null);
  const hasInitialized = useRef(false);
  const hasActivated = useRef(false);

  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);

  // Unless manually specified, the observer should trigger as soon as the
  // attached element scrolls into view
  options ??= {
    root: null,
    threshold: 0.1,
  };

  useEffect(() => {
    if (elementRef.current && !hasInitialized.current) {
      const observer = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;

        if (entry.isIntersecting) {
          setHasScrolledIntoView(true);

          // This case only needs to be hit once, so we can disconnect
          // the observer at this point
          observer.disconnect();
        }
      }, options);

      observer.observe(elementRef.current);

      hasInitialized.current = true;

      return () => observer.disconnect();
    }
  }, [elementRef]);

  useEffect(() => {
    if (hasScrolledIntoView && !hasActivated.current) {
      activateOptimizelyExperiment(pageName).then(() => {
        hasActivated.current = true;
      });
    }
  }, [hasScrolledIntoView]);

  return elementRef;
}
